/**
 * ResetPassword
 *
 * Public page reached from the email link /reset-password?token=...
 * On submit, calls POST /api/auth/reset-password which returns a full
 * auth payload (we sign the user in immediately).
 */
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [pwd, setPwd] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const missingToken = !token;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pwd.length < 8 || !/[a-zA-Z]/.test(pwd) || !/[0-9]/.test(pwd)) {
      setError("Password must be at least 8 characters and include at least 1 letter and 1 number.");
      return;
    }
    if (pwd !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      // Auto-login -- the backend set cookies for us. Refresh auth state.
      await refresh();
      navigate("/profile", { replace: true });
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-12 md:py-20">
      <Card elevated>
        <CardHeader>
          <div>
            <h1 className="text-2xl font-black tracking-tight">Set a new password</h1>
            <p className="text-ink-muted text-sm mt-1">
              Choose something memorable. Minimum 8 characters.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          {missingToken ? (
            <div
              data-testid="reset-missing-token"
              className="rounded-2xl border-2 border-brand bg-brand-soft p-5 text-center"
            >
              <h2 className="text-lg font-bold text-brand">Missing reset token</h2>
              <p className="text-sm text-ink mt-1">
                Please open the link from your reset email again, or{" "}
                <Link to="/forgot-password" className="underline font-semibold">
                  request a new one
                </Link>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="reset-form">
              <Input
                label="New password"
                type="password"
                autoFocus
                minLength={8}
                required
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                hint="At least 8 characters, including a letter and a number."
                data-testid="reset-password"
              />
              <Input
                label="Confirm password"
                type="password"
                minLength={8}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm"
              />
              {error && (
                <p
                  data-testid="reset-error"
                  role="alert"
                  className="text-sm font-semibold text-brand"
                >
                  {error}
                </p>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                leadingIcon={<ShieldCheck className="h-5 w-5" />}
                data-testid="reset-submit"
              >
                Reset password & sign in
              </Button>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
